// src/pages/PatientLoginPage.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  authenticatePatient, 
  addPatient, 
  getPatientSessions, 
  getSurgeriesByInstitution,
  getDoctorsByInstitution,
  addPatientSession,
  validateInstitutionNumber,
  addPatientToSurgery
} from '../services/firebase';
import { Patient, PatientSession, Surgery, Doctor, PatientSpecialConditions } from '../types';
import PatientConditionsModal from '../components/PatientConditionsModal';
import { Language, tt, useTranslation } from '../i18n';

const generateCustomizedSections = async (
  originalSections: any[],
  specialConditions: PatientSpecialConditions,
  patientInfo: { name: string },
  language: Language // language 인자 추가
) => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  if (!apiKey) {
    // 🔥 수정: tt 함수를 사용하여 번역된 에러 메시지 사용
    throw new Error(tt('services.pdf.api_key_not_set', undefined, language));
  }

  // 🔥 수정: formatSpecialConditions 함수를 generateCustomizedSections 내부로 이동
  const formatSpecialConditions = (conditions: PatientSpecialConditions): string => {
    // 이 함수의 내용은 이전과 동일하게 유지됩니다.
    const sections = [];
    if (conditions.resectionType) sections.push(`Resection Type: ${conditions.resectionType}`);
    if (conditions.cancerStage) sections.push(`Cancer Stage: ${conditions.cancerStage}`);
    if (conditions.reconstructionType) sections.push(`Reconstruction: ${conditions.reconstructionType}`);
    if (conditions.otherNotes?.trim()) sections.push(`Other Notes: ${conditions.otherNotes}`);
    return sections.length > 0 ? sections.join('\n') : 'No special conditions noted.';
  };

  const sectionsText = originalSections
    .map((section, index) => `## Section ${index + 1}: ${section.title}\n${section.content}`)
    .join('\n\n');

  const specialConditionsText = formatSpecialConditions(specialConditions);

  // 🔥 수정: 하드코딩된 프롬프트 대신 tt 함수를 사용하여 동적으로 프롬프트 생성
  const systemPrompt = tt('prompts.customizedSections.system', undefined, language);
  const userPrompt = tt('prompts.customizedSections.user', {
    sectionsText,
    patientName: patientInfo.name,
    specialConditionsText,
  }, language);

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          systemInstruction: {
            parts: [{ text: systemPrompt }]
          },
          contents: [
            {
              role: 'user',
              parts: [{ text: userPrompt }]
            }
          ],
          generationConfig: {
            temperature: 0.3,
            maxOutputTokens: 4000,
            responseMimeType: 'application/json'
          }
        })
      }
    );

    if (!response.ok) {
      throw new Error(`Gemini API Error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

    let cleanedContent = content.trim();
    if (cleanedContent.startsWith('```json')) {
      cleanedContent = cleanedContent.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanedContent.startsWith('```')) {
      cleanedContent = cleanedContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    const result = JSON.parse(cleanedContent);
    return result.customizedSections;

  } catch (error) {
    console.error('맞춤형 섹션 생성 오류:', error);
    throw error;
  }
};

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px 32px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 400px;
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const LogoText = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 16px;
  background-color: white;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    outline: none;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'secondary' 
    ? `
      background-color: #f3f4f6;
      color: #374151;
      &:hover { background-color: #e5e7eb; }
    `
    : `
      background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
      color: white;
      &:hover { transform: translateY(-1px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
    `
  }
  
  &:active { transform: translateY(0); }
  &:disabled { 
    opacity: 0.5; 
    cursor: not-allowed; 
    transform: none !important;
    box-shadow: none !important;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: #166534;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
`;

const SessionList = styled.div`
  margin-bottom: 24px;
`;

const SessionItem = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f1f5f9;
    border-color: #0369a1;
    transform: translateY(-1px);
  }
`;

const SessionTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const SessionInfo = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const NewSessionButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: none;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #0369a1;
    color: #0369a1;
    background-color: #f9fafb;
  }
`;

const InstitutionInfo = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
  text-align: center;
`;

const InstitutionText = styled.div`
  font-size: 12px;
  color: #0369a1;
  font-weight: 500;
`;

const InstitutionInputSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background-color: #fef3c7;
  border: 1px solid #fed7aa;
  border-radius: 12px;
`;

const InstitutionInputTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
  text-align: center;
`;

const ConditionsButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  
  &:hover {
    border-color: #0369a1;
    background-color: #f8fafc;
  }
`;

const ConditionsSummary = styled.div`
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
`;

const ConditionsTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 4px;
`;

const ConditionsText = styled.div`
  font-size: 11px;
  color: #15803d;
  line-height: 1.4;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: black;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  color: white;
  flex-direction: column;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 18px;
  font-weight: 600;
  text-align: center;
`;

const LoadingSubText = styled.div`
  font-size: 14px;
  text-align: center;
  opacity: 0.8;
  white-space: pre-line;
`;

const PatientLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  
  const [institutionNumber, setInstitutionNumber] = useState('');
  const [institutionValidated, setInstitutionValidated] = useState(false);
  const [validatingInstitution, setValidatingInstitution] = useState(false);
  
  const [loginData, setLoginData] = useState({ name: '' });
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const [sessions, setSessions] = useState<PatientSession[]>([]);
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionData, setNewSessionData] = useState({
    surgeryName: '',
    doctorId: ''
  });
  
  const [specialConditions, setSpecialConditions] = useState<PatientSpecialConditions>({
    resectionType: '',
    cancerStage: '',
    reconstructionType: '',
    otherNotes: '',
    unknownStates: {
      resectionType: false,
      cancerStage: false,
      reconstructionType: false
    }
  });
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  
  const [consentApi, setConsentApi] = useState(false);
  const [consentDisclaimer, setConsentDisclaimer] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isGeneratingCustomSections, setIsGeneratingCustomSections] = useState(false);

  // 닉네임 입력 처리 함수 (영어 소문자 변환)
  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase(); // 영어를 자동으로 소문자로 변환
    setLoginData(prev => ({ ...prev, name: value }));
  };

  const handleInstitutionValidation = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidatingInstitution(true);
    setError('');

    try {
      if (!institutionNumber.trim()) {
        setError(t('patientLogin.errors.instNumberRequired'));
        return;
      }

      const isValid = await validateInstitutionNumber(institutionNumber.trim());
      
      if (isValid) {
        setInstitutionValidated(true);
        setSuccess(t('patientLogin.success.instValidated'));
        await loadInitialData(institutionNumber.trim());
      } else {
        setError(t('patientLogin.errors.invalidInstNumber'));
      }
    } catch (error) {
      console.error('기관번호 검증 오류:', error);
      setError(t('patientLogin.errors.instValidationFailed'));
    } finally {
      setValidatingInstitution(false);
    }
  };

  const loadInitialData = async (validInstitutionNumber: string) => {
    try {
      const [surgeriesData, doctorsData] = await Promise.all([
        getSurgeriesByInstitution(validInstitutionNumber),
        getDoctorsByInstitution(validInstitutionNumber)
      ]);
      setSurgeries(surgeriesData);
      setDoctors(doctorsData);
    } catch (error) {
      console.error('초기 데이터 로드 오류:', error);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const { name } = loginData;
      const trimmedName = name.trim().toLowerCase(); // 최종적으로도 소문자 보장
      
      if (!trimmedName) {
        setError(t('patientLogin.errors.nicknameRequired'));
        return;
      }

      const authenticatedPatient = await authenticatePatient(institutionNumber, trimmedName);
      
      if (authenticatedPatient) {
        setPatient(authenticatedPatient);
        setIsAuthenticated(true);
        setSuccess(t('patientLogin.success.login'));
        const patientSessions = await getPatientSessions(institutionNumber, authenticatedPatient.name);
        setSessions(patientSessions);
      } else {
        const patientData: Omit<Patient, 'id'> = {
          institutionNumber,
          name: trimmedName // 소문자로 변환된 이름 사용
        };
        const result = await addPatient(patientData);
        const newPatient: Patient = { id: result.id, ...patientData };
        setPatient(newPatient);
        setIsAuthenticated(true);
        setSuccess(t('patientLogin.success.register'));
        setSessions([]);
      }
    } catch (error) {
      console.error('로그인/회원가입 오류:', error);
      setError(t('patientLogin.errors.processingFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionClick = (session: PatientSession) => {
    const encodedSurgeryName = encodeURIComponent(session.surgeryName);
    const sessionUrl = session.doctorId && session.doctorId !== 'general'
      ? `/patient/${encodedSurgeryName}/${session.patientName}/${session.doctorId}`
      : `/patient/${encodedSurgeryName}/${session.patientName}`;
    sessionStorage.setItem('institutionNumber', institutionNumber);
    navigate(sessionUrl);
  };

  const handleConditionsSave = (conditions: PatientSpecialConditions) => {
    setSpecialConditions(conditions);
    setShowConditionsModal(false);
  };

  const getConditionsSummary = () => {
    let totalCount = 0;
    const summaryParts: string[] = [];
    const conditionKeys = ['resectionType', 'cancerStage', 'reconstructionType'];
    conditionKeys.forEach(key => {
        if (specialConditions[key as keyof PatientSpecialConditions]) {
            totalCount++;
        }
    });
    if (specialConditions.otherNotes?.trim()) totalCount++;

    const unknownCount = Object.values(specialConditions.unknownStates || {}).filter(Boolean).length;
    if (unknownCount > 0) {
      summaryParts.push(`${t('patientLogin.conditionsSummary_unknown', { count: unknownCount })}`);
    }
    
    if (totalCount === 0 && unknownCount === 0) {
      return t('patientLogin.conditionsSummary_none');
    }
    
    return `${t('patientLogin.conditionsSummary_filled', { count: totalCount + unknownCount })} ${summaryParts.length > 0 ? `(${summaryParts.join(', ')})` : ''}`;
  };

  const handleCreateNewSession = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (!newSessionData.surgeryName || !patient) {
        setError(t('patientLogin.errors.surgeryRequired'));
        return;
      }
      if (!consentApi || !consentDisclaimer) {
        setError(t('patientLogin.errors.consentRequired'));
        return;
      }

      const selectedSurgery = surgeries.find(s => s.name === newSessionData.surgeryName);
      if (!selectedSurgery) {
        setError(t('patientLogin.errors.surgeryNotFound'));
        return;
      }

      setIsGeneratingCustomSections(true);
      
      let customizedSections = [];
      
      const hasSpecialConditions = 
        specialConditions.resectionType ||
        specialConditions.cancerStage ||
        specialConditions.reconstructionType ||
        (specialConditions.otherNotes && specialConditions.otherNotes.trim());

      if (hasSpecialConditions) {
        try {
          customizedSections = await generateCustomizedSections(
            selectedSurgery.sections,
            specialConditions,
            { name: patient.name },
            language 
          );
        } catch (sectionError) {
          customizedSections = selectedSurgery.sections;
        }
      } else {
        customizedSections = selectedSurgery.sections;
      }

      setIsGeneratingCustomSections(false);

      const sessionData: Omit<PatientSession, 'id'> = {
        patientName: patient.name,
        institutionNumber,
        surgeryName: newSessionData.surgeryName,
        doctorId: newSessionData.doctorId || 'general',
        specialConditions,
        customizedSections,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addPatientSession(sessionData);
      
      try {
        await addPatientToSurgery(newSessionData.surgeryName, patient.id);
      } catch (surgeryError) {
        console.error('수술에 환자 추가 실패:', surgeryError);
      }

      const encodedSurgeryName = encodeURIComponent(newSessionData.surgeryName);
      const sessionUrl = newSessionData.doctorId
        ? `/patient/${encodedSurgeryName}/${patient.name}/${newSessionData.doctorId}`
        : `/patient/${encodedSurgeryName}/${patient.name}`;
      
      sessionStorage.setItem('institutionNumber', institutionNumber);
      navigate(sessionUrl);
      
    } catch (err) {
      console.error('새 세션 생성 오류:', err);
      setError(t('patientLogin.errors.sessionCreateFailed'));
    } finally {
      setIsLoading(false);
      setIsGeneratingCustomSections(false);
    }
  };

  const handleLogout = () => {
    setPatient(null);
    setIsAuthenticated(false);
    setSessions([]);
    setShowNewSession(false);
    setLoginData({ name: '' });
    setSpecialConditions({
      resectionType: '', cancerStage: '', reconstructionType: '', otherNotes: '',
      unknownStates: { resectionType: false, cancerStage: false, reconstructionType: false }
    });
    setError('');
    setSuccess('');
  };

  const handleChangeInstitution = () => {
    setInstitutionNumber('');
    setInstitutionValidated(false);
    setPatient(null);
    setIsAuthenticated(false);
    setSessions([]);
    setShowNewSession(false);
    setLoginData({ name: '' });
    setError('');
    setSuccess('');
  };

  if (!institutionValidated) {
    return (
      <Container>
        <LoginCard>
          <Header>
            <Logo>
              <img src="/osler_logo.png" alt="Osler" style={{ height: '32px' }} />
              <LogoText>{t('patientLogin.title')}</LogoText>
            </Logo>
            <Subtitle>{t('patientLogin.subtitle')}</Subtitle>
          </Header>

          <InstitutionInputSection>
            <InstitutionInputTitle>{t('patientLogin.instInputSectionTitle')}</InstitutionInputTitle>
            <form onSubmit={handleInstitutionValidation}>
              <FormGroup>
                <Label htmlFor="institutionNumber">{t('patientLogin.instInputLabel')}</Label>
                <Input
                  id="institutionNumber" type="text" value={institutionNumber}
                  onChange={(e) => setInstitutionNumber(e.target.value)}
                  placeholder={t('patientLogin.instInputPlaceholder')}
                  disabled={validatingInstitution}
                />
              </FormGroup>

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}

              <Button type="submit" disabled={validatingInstitution || !institutionNumber.trim()}>
                {validatingInstitution ? t('patientLogin.instValidatingButton') : t('patientLogin.instValidateButton')}
              </Button>
            </form>
          </InstitutionInputSection>
        </LoginCard>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container>
        <LoginCard>
          <Header>
            <Logo>
              <img src="/osler_logo.png" alt="Osler" style={{ height: '32px' }} />
              <LogoText>{t('patientLogin.title')}</LogoText>
            </Logo>
            <Subtitle>{t('patientLogin.subtitle')}</Subtitle>
          </Header>

          <InstitutionInfo>
            <InstitutionText>
              {t('patientLogin.instInfo', { number: institutionNumber })}
              <button onClick={handleChangeInstitution}
                style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#0369a1', textDecoration: 'underline', cursor: 'pointer', fontSize: '10px' }}
              >
                {t('patientLogin.changeInst')}
              </button>
            </InstitutionText>
          </InstitutionInfo>

          <FormSection>
            <SectionTitle>{t('patientLogin.nicknameSectionTitle')}</SectionTitle>
            <form onSubmit={handleLogin}>
              <FormGroup>
                <Label htmlFor="name">{t('patientLogin.nicknameLabel')}</Label>
                <Input
                  id="name" 
                  type="text" 
                  value={loginData.name}
                  onChange={handleNicknameChange}
                  placeholder={t('patientLogin.nicknamePlaceholder')}
                  disabled={isLoading}
                />
              </FormGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}
              <FormGroup>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? t('common.processing') : t('patientLogin.startButton')}
                </Button>
              </FormGroup>
            </form>
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '12px', color: '#166534', textAlign: 'center' }}>
              {t('patientLogin.autoRegisterInfo_line1')}<br />
              {t('patientLogin.autoRegisterInfo_line2')}
            </div>
          </FormSection>
        </LoginCard>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <LoginCard>
          <Header>
            <Logo>
              <img src="/osler_logo.png" alt="Osler" style={{ height: '28px' }} />
              <LogoText>{t('patientLogin.welcome', { name: patient?.name })}</LogoText>
            </Logo>
            <Subtitle>{t('patientLogin.sessionSelectTitle')}</Subtitle>
          </Header>

          {!showNewSession ? (
            <FormSection>
              <SectionTitle>{t('patientLogin.previousSessionsTitle')}</SectionTitle>
              
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>{t('patientLogin.loading')}</div>
              ) : sessions.length > 0 ? (
                <SessionList>
                  {sessions.map(session => {
                    const doctor = doctors.find(d => d.id === session.doctorId);
                    const lastUpdateDate = session.updatedAt ? (language === 'ko' ? session.updatedAt.toLocaleDateString('ko-KR') : session.updatedAt.toLocaleDateString('en-US')) : '';
                    return (
                      <SessionItem key={session.id} onClick={() => handleSessionClick(session)}>
                        <SessionTitle>{session.surgeryName}</SessionTitle>
                        <SessionInfo>
                          {t('patientLogin.sessionInfo.doctor')}: {session.doctorId === 'general' || !session.doctorId ? t('patientLogin.sessionInfo.doctor_general') : doctor?.name || t('patientLogin.sessionInfo.doctor_unknown')}
                          {session.updatedAt && ` | ${t('patientLogin.sessionInfo.lastSession')}: ${lastUpdateDate}`}
                          {session.customizedSections && ` | ${t('patientLogin.sessionInfo.customized')}`}
                        </SessionInfo>
                      </SessionItem>
                    );
                  })}
                </SessionList>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>{t('patientLogin.emptySessions.title')}</div>
                  <div style={{ fontSize: '14px' }}>{t('patientLogin.emptySessions.desc')}</div>
                </div>
              )}

              <NewSessionButton onClick={() => setShowNewSession(true)}>
                {t('patientLogin.newSessionButton')}
              </NewSessionButton>

              <div style={{ marginTop: '24px' }}>
                <Button type="button" variant="secondary" onClick={handleLogout}>
                  {t('common.logout')}
                </Button>
              </div>
            </FormSection>
          ) : (
            <FormSection>
              <SectionTitle>{t('patientLogin.newSessionTitle')}</SectionTitle>
              
              <form onSubmit={handleCreateNewSession}>
                <FormGroup>
                  <Label htmlFor="surgeryName">{t('patientLogin.fields.surgery')}</Label>
                  <Select id="surgeryName" value={newSessionData.surgeryName} onChange={(e) => setNewSessionData(prev => ({ ...prev, surgeryName: e.target.value }))} disabled={isLoading}>
                    <option value="">{t('patientLogin.placeholders.surgery')}</option>
                    {surgeries.map(surgery => (<option key={surgery.id} value={surgery.name}>{surgery.name}</option>))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="doctorId">{t('patientLogin.fields.doctor')}</Label>
                  <Select id="doctorId" value={newSessionData.doctorId} onChange={(e) => setNewSessionData(prev => ({ ...prev, doctorId: e.target.value }))} disabled={isLoading}>
                    <option value="">{t('patientLogin.placeholders.doctor')}</option>
                    {doctors.map(doctor => (<option key={doctor.id} value={doctor.id}>{doctor.name} ({doctor.doctorNumber})</option>))}
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>{t('patientLogin.fields.conditions')}</Label>
                  <ConditionsButton type="button" onClick={() => setShowConditionsModal(true)}>{t('patientLogin.conditionsButton')}</ConditionsButton>
                  <ConditionsSummary>
                    <ConditionsTitle>{t('patientLogin.conditionsSummaryTitle')}</ConditionsTitle>
                    <ConditionsText>{getConditionsSummary()}</ConditionsText>
                  </ConditionsSummary>
                </FormGroup>

                <FormGroup>
                  <CheckboxLabel>
                    <input type="checkbox" checked={consentApi} onChange={(e) => setConsentApi(e.target.checked)} />
                    <span style={{ fontSize: '13px', lineHeight: 1.4 }}>{t('patientLogin.consent_api')}</span>
                  </CheckboxLabel>
                </FormGroup>
                <FormGroup>
                  <CheckboxLabel>
                    <input type="checkbox" checked={consentDisclaimer} onChange={(e) => setConsentDisclaimer(e.target.checked)} />
                    <span style={{ fontSize: '13px', lineHeight: 1.4 }}>{t('patientLogin.consent_disclaimer')}</span>
                  </CheckboxLabel>
                </FormGroup>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <FormGroup>
                  <Button type="submit" disabled={isLoading || !newSessionData.surgeryName}>
                    {isLoading ? t('patientLogin.creatingSessionButton') : t('patientLogin.createSessionButton')}
                  </Button>
                </FormGroup>

                <Button type="button" variant="secondary" onClick={() => setShowNewSession(false)} disabled={isLoading}>
                  {t('common.back')}
                </Button>
              </form>
            </FormSection>
          )}

          <PatientConditionsModal
            isOpen={showConditionsModal}
            onClose={() => setShowConditionsModal(false)}
            onSave={handleConditionsSave}
            initialConditions={specialConditions}
          />
        </LoginCard>
      </Container>

      {isGeneratingCustomSections && (
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>{t('patientLogin.generatingCustomSections.title')}</LoadingText>
          <LoadingSubText>
            {t('patientLogin.generatingCustomSections.desc_line1')}{'\n'}
            {t('patientLogin.generatingCustomSections.desc_line2')}{'\n'}
            {t('patientLogin.generatingCustomSections.desc_line3')}
          </LoadingSubText>
        </LoadingOverlay>
      )}
    </>
  );
};

export default PatientLoginPage;